import React from "react";
import { Activity } from "lucide-react";
import { POSITION_CONFIG } from "./constants";
import RiskBadge from "./RiskBadge";

const PlayerCard = ({ player, onSelect, isSelected, isDarkMode }) => {

const trainability = player.avg_trainability_10; 

const status =
  player?.trainability_level?.global ??
  player?.trainability_level ??
  null;

const getTrainabilityColor = () => {
  if (!Number.isFinite(trainability)) return "slate";

  // règle spéciale
  if (trainability < 4) {
    if (status === "underload") return "blue";
    if (status === "overload") return "rose"; // rouge
    return "blue";
  }

  // zones normales
  if (trainability >= 6) return "emerald";
  if (trainability >= 4) return "amber";
  return "rose";
};



  const trainColor = getTrainabilityColor();
  const posConfig = POSITION_CONFIG[player.position] || { color: "gray" };

  const trainabilityGradient = {
    emerald: "from-emerald-500 to-emerald-600",
    amber: "from-amber-500 to-amber-600",
    rose: "from-rose-500 to-rose-600",
    blue: "from-blue-500 to-blue-600",
  }[trainColor];

  const positionBadgeColor = {
    amber: isDarkMode ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700",
    blue: isDarkMode ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700",
    emerald: isDarkMode ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700",
    rose: isDarkMode ? "bg-rose-500/20 text-rose-400" : "bg-rose-100 text-rose-700",
    gray: isDarkMode ? "bg-gray-500/20 text-gray-400" : "bg-gray-100 text-gray-700",
  }[posConfig.color];

  return (
    <button
      onClick={() => onSelect(player)}
      className={`
        group relative overflow-hidden text-left w-full transition-all duration-300
        rounded-2xl backdrop-blur-sm
        ${isSelected
          ? isDarkMode 
            ? "bg-white/10 ring-2 ring-white/20 shadow-xl shadow-black/20" 
            : "bg-white ring-2 ring-indigo-200 shadow-xl shadow-indigo-500/10"
          : isDarkMode
            ? "bg-white/5 hover:bg-white/10 hover:shadow-lg hover:shadow-black/10"
            : "bg-white/80 hover:bg-white hover:shadow-lg hover:shadow-gray-200/50"
        }
      `}
    >
      {/* Trainability indicator bar at top */}
      <div className="h-1 w-full overflow-hidden">
        <div 
          className={`h-full bg-gradient-to-r ${trainabilityGradient} transition-all duration-500`}
          style={{ width: `${Math.min(100, Math.max(0, trainability * 10))}%` }}
        />
      </div>

      <div className="p-3">
        {/* Top: Avatar + Name + Position */}
        <div className="flex items-start gap-3">
          {/* Avatar with gradient ring */}
          <div className="relative">
            {player.profile_picture ? (
              <img
                src={`data:image/jpeg;base64,${player.profile_picture}`}
                alt={`${player.first_name} ${player.last_name}`}
                className="w-12 h-12 rounded-xl object-cover transition-transform duration-300 group-hover:scale-105"
              />
            ) : (
              <div
                className={`
                  w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold
                  transition-transform duration-300 group-hover:scale-105
                  ${isDarkMode 
                    ? "bg-gradient-to-br from-gray-700 to-gray-800 text-white" 
                    : "bg-gradient-to-br from-gray-50 to-gray-100 text-gray-700 shadow-inner"}
                `}
              >
                {player.first_name?.[0]}{player.last_name?.[0]}
              </div>
            )}
            {/* Jersey number badge */}
            <div className={`
              absolute -bottom-1 -right-1 w-5 h-5 rounded-md flex items-center justify-center
              text-[10px] font-bold
              ${isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-700 shadow-sm"}
            `}>
              {player.jersey_number}
            </div>
          </div>

          {/* Name + Position */}
          <div className="flex-1 min-w-0 pt-0.5">
            <div className="font-semibold text-sm truncate leading-tight">
              {player.first_name}
            </div>
            <div className="font-bold text-sm truncate">
              {player.last_name}
            </div>
            <span className={`inline-block mt-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${positionBadgeColor}`}>
              {player.position}
            </span>
          </div>

          {/* Trainability score - big */}
          <div className="text-right">
            <div className={`text-2xl font-black bg-gradient-to-r ${trainabilityGradient} bg-clip-text text-transparent`}>
              {trainability}
            </div>
            <div className={`text-[9px] uppercase tracking-wider ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
              trainability
            </div>
          </div>
        </div>

        {/* Bottom: Stats row */}
        <div className={`
          mt-3 pt-2 flex items-center justify-between gap-2
          border-t ${isDarkMode ? "border-white/5" : "border-gray-100"}
        `}>
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1">
              <Activity size={12} className={isDarkMode ? "text-gray-500" : "text-gray-400"} />
              <span className="font-medium">{player.total_distance_km}km</span>
            </div>
          </div>
          {/* <RiskBadge risk={player.injury_risk} isDarkMode={isDarkMode} /> */}
        </div>
      </div>
    </button>
  );
};

export default PlayerCard;
